import { type FC, useMemo } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { SelectDropdown, type SelectOption } from '../../../../../components/SelectDropdown/SelectDropdown'
import { useGetAllMeetingTemplatesQuery } from '../../../../../store/ai/meetingTemplate'
import { useAppSelector } from '../../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../../store/auth/authSlice'
import { type SchedulingEventType } from '../../../../../models/Scheduling'

interface DetailsTabProps {
  draft: SchedulingEventType
  disabled: boolean
  onDraftChange: (patch: Partial<SchedulingEventType>) => void
}

export const DetailsTab: FC<DetailsTabProps> = ({ draft, disabled, onDraftChange }) => {
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const { data: meetingTemplates, isFetching } = useGetAllMeetingTemplatesQuery({
    accountId: authenticatedUser.accountId,
    includeInactive: false,
  })

  const options: SelectOption[] = useMemo(
    () => (meetingTemplates?.data ?? []).map((template) => ({ id: template.id, label: template.name })),
    [meetingTemplates],
  )
  const selected = options.find((option) => option.id === draft.connectedMeetingTypeId) ?? null

  return (
    <div className='d-flex flex-column gap-4'>
      <Row className='gy-2'>
        <Col md={5}>
          <label className='bold'>Connected meeting type</label>
          <div className='paragraph-small zocks-text-gray-600'>Tells your AI Assistant how to take notes for this meeting.</div>
        </Col>
        <Col md={7}>
          <SelectDropdown
            options={options}
            initialSelectedOption={selected}
            placeholder='Select meeting type'
            loading={isFetching}
            disabled={disabled}
            showDeselectOption
            deselectOptionLabel='None'
            onOptionSelected={(option) => onDraftChange({ connectedMeetingTypeId: option.id })}
            onDeselect={() => onDraftChange({ connectedMeetingTypeId: undefined })}
          />
        </Col>
      </Row>
      <Row className='gy-2'>
        <Col md={5}>
          <label className='bold' htmlFor='event-type-client-text'>
            Client-facing text
          </label>
          <div className='paragraph-small zocks-text-gray-600'>Shown to clients on the booking page below the event type name.</div>
        </Col>
        <Col md={7}>
          <Form.Control
            as='textarea'
            id='event-type-client-text'
            rows={4}
            value={draft.clientFacingText}
            disabled={disabled}
            placeholder='e.g. A yearly check-in to review your finances and plan for the year ahead.'
            onChange={(event) => onDraftChange({ clientFacingText: event.currentTarget.value })}
          />
        </Col>
      </Row>
    </div>
  )
}
